import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { resetLogoutState } from "../services/api";
import { limparCache, recuperarCache, salvarCache, salvarCacheCriptografado } from "../services/cacheHandler";
import NotificacaoUtils from "../utils/NotificacaoUtils";
import { APP_ROUTES } from "../services/app_routes";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {

  const [carregando, setCarregando] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const logoutInProgress = useRef(false);

  const [user, setUser] = useState(() => {
    if (typeof window !== 'undefined') {
      const cachedUser = recuperarCache("usuario");
      if (cachedUser) {
        return cachedUser.usuarioTO || cachedUser;
      }
    }
    return null;
  });

  const login = async (email, senha) => {
    setCarregando(true);
    
    try {
      const apiResponse = await api.post("/login_", { login: email, senha }, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!apiResponse.success) {
        setCarregando(false);
        NotificacaoUtils.erro(apiResponse.message);
        return;
      }

      const usuario = apiResponse.data;

      setUser(usuario.usuarioTO);
      salvarCacheCriptografado(
        'permissoes',
        defineMapPermissoes(usuario.usuarioTO.listaPermissoes)
      );
      salvarCacheCriptografado("usuario", usuario);
      salvarCache("access_token", usuario.token);

      // Reset do flag de logout quando faz login com sucesso
      logoutInProgress.current = false;
      resetLogoutState(); // Resetar o estado de logout no serviço de API
      navigate(APP_ROUTES.HOME, { replace: true });
      
    } catch (error) {
      setError(error.message);
    } finally {
      setCarregando(false);
    }
  };

  const logout = useCallback(() => {
    if (logoutInProgress.current) return;
    
    logoutInProgress.current = true;
    
    setUser(null);
    limparCache();
    
    navigate(APP_ROUTES.LOGIN, { replace: true });
    
    setTimeout(() => {
      logoutInProgress.current = false;
      resetLogoutState();
    }, 1000);
  }, [navigate]);

  const defineMapPermissoes = (permissoes) => {
    const mapPermissoes = new Map();
    permissoes.forEach((permissao) =>  mapPermissoes.set(permissao, true));
    return mapPermissoes;
  }

  useEffect(() => {
    setCarregando(false);
  }, []);

  // Listener para o evento de expiração de token (evento específico)
  useEffect(() => {
    const handleTokenExpiredEvent = () => {
      logout();
    };
    
    window.addEventListener("token-expired", handleTokenExpiredEvent);
    return () => {
      window.removeEventListener("token-expired", handleTokenExpiredEvent);
    };
  }, [logout]);

  useEffect(() => {
    const handleLogoutEvent = () => {
      logout();
    };
    window.addEventListener("logout", handleLogoutEvent);
    return () => {
      window.removeEventListener("logout", handleLogoutEvent);
    };
  }, [logout]);

  const contextValue = useMemo(() => ({
    login,
    logout,
    user,
    carregando,
    error,
  }), [login, logout, user, carregando, error]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
